import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe, User, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/visa-finder", label: "Visa Finder" },
  ];

  const servicesLinks = [
    { path: "/evisa", label: "eVisa Services" },
    { path: "/oci", label: "OCI Card (India)" },
    { path: "/pcc", label: "Police Clearance" },
  ];

  const isActive = (path) => location.pathname === path;
  const isServicesActive = servicesLinks.some(link => location.pathname === link.path);

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200/50 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="navbar-logo"
          >
            <Globe className="w-8 h-8 text-[#E15636] transition-transform group-hover:rotate-12" />
            <span className="font-heading text-2xl text-[#0F172A]">VisaFlows</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-[#0F172A] ${
                  isActive(link.path) 
                    ? "text-[#0F172A] font-semibold" 
                    : "text-[#64748B]"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className={`flex items-center gap-1 text-sm font-medium transition-colors hover:text-[#0F172A] ${
                isServicesActive ? "text-[#0F172A] font-semibold" : "text-[#64748B]"
              }`}>
                Services
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {servicesLinks.map((link) => (
                  <DropdownMenuItem key={link.path} asChild>
                    <Link 
                      to={link.path}
                      className={`w-full cursor-pointer ${
                        isActive(link.path) ? "font-semibold" : ""
                      }`}
                    >
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link
              to="/terms"
              className={`text-sm font-medium transition-colors hover:text-[#0F172A] ${
                isActive("/terms") 
                  ? "text-[#0F172A] font-semibold" 
                  : "text-[#64748B]"
              }`}
              data-testid="nav-link-terms"
            >
              Terms
            </Link>
          </div>

          {/* Auth Button */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/auth" data-testid="auth-button">
              <Button 
                variant="outline" 
                className="rounded-full border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-all"
              >
                <User className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#0F172A]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="md:hidden py-4 border-t border-slate-200"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-base font-medium py-2 transition-colors ${
                    isActive(link.path) 
                      ? "text-[#0F172A] font-semibold" 
                      : "text-[#64748B]"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Services Section */}
              <div className="border-t border-slate-200 pt-4">
                <p className="text-xs uppercase text-[#64748B] mb-2 tracking-widest">Services</p>
                {servicesLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-base font-medium py-2 block transition-colors ${
                      isActive(link.path) 
                        ? "text-[#0F172A] font-semibold" 
                        : "text-[#64748B]"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              <Link
                to="/terms"
                className={`text-base font-medium py-2 transition-colors ${
                  isActive("/terms") 
                    ? "text-[#0F172A] font-semibold" 
                    : "text-[#64748B]"
                }`}
                onClick={() => setIsOpen(false)}
              >
                Terms
              </Link>

              <Link 
                to="/auth" 
                onClick={() => setIsOpen(false)}
                data-testid="mobile-auth-button"
              >
                <Button className="w-full rounded-full bg-[#0F172A] text-white">
                  <User className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
